/* eslint-disable max-lines -- I can't find any logical way to split this file */
import { inject, Inject, Injectable } from '@angular/core';
import { Store } from '@sama/app-state';
import { assert, isDefined, mapToObject } from '@sama/js-core';
import { isEmpty, keyBy, matches, omit, some } from '@sama/micro-dash';
import { isUndefined } from 'lodash';
import { combineLatest } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { FusionState } from '../../../../core/h3ml/state/fusion-state';
import { H3mlStore } from '../../../../core/h3ml/state/h3ml-store';
import {
  OutputInfo,
  outputInfoStoreToken,
} from '../../../../core/h3ml/state/output-info';
import {
  fusionStoreToken,
  TaskWorkspaceStore,
} from '../../../../core/h3ml/state/task-workspace-store';
import { FusionManager } from '../../../../core/outputs/annotation/annotation-shared/fusion/fusion-manager';
import { FusionManagerFactory } from '../../../../core/outputs/annotation/annotation-shared/fusion/fusion-manager-factory.service';
import { TabName } from '../../../../core/outputs/annotation/annotation-shared/state/layers/transient-shape-layer-state';
import { ObjectlessFocusedShapeService } from '../../../../core/outputs/annotation/annotation-shared/utils/objectless-focused-shape.service';
import { getShapesForEventTracking } from '../../../../cross-team-shared/annotation/shared/state/layers/shape-layer-state';
import {
  DynamicTags,
  ShapeState,
  StaticTags,
  TagsState,
} from '../../../../cross-team-shared/annotation/shared/state/shape-state';
import {
  ensureKeyDynamicTag,
  removeKeyframe,
} from '../../../../cross-team-shared/annotation/shared/utils/keyframe-utils';
import {
  getColorOutput,
  getDynamicTag,
  getShapeColorObjectless$,
  getTag$,
  isDynamicOutput,
} from '../../../../cross-team-shared/misc-services/tag-helper';
import { isImageAnnotationOutput } from '../../../../cross-team-shared/misc-utils/output-utils';
import { ObjectWith } from '../../../../cross-team-shared/types/misc';
import { EventTrackingService } from '../../../../shared/event-tracking/event-tracking.service';
import { getOutputHelper } from '../../../../shared/output-helpers/get-output-helper';
import { OutputHelper } from '../../../../shared/output-helpers/output-helper';
import { AnswerOption } from '../../../../team-apis/otterdam/answer-option';
import {
  Answer,
  AnswersByName,
} from '../../../../team-apis/otterdam/answers/answer';
import { FrameSpecificTag } from '../../../../team-apis/otterdam/answers/shape-typings';
import { AnnotationOutput } from '../../../../team-apis/otterdam/outputs/annotation-output';
import {
  AbstractOutput,
  AbstractOutputWithAnswerOptions,
  Output,
} from '../../../../team-apis/otterdam/outputs/output';
import { AbstractAnnotationStore } from '../state/root/abstract-annotation-store';

@Injectable()
/** @deprecated used for old projects without objects */
export class TagService {
  private readonly taskWorkspaceStore = inject(TaskWorkspaceStore);
  // for some obscure reason, it's impossible to inject shapeInfoStoreToken
  private readonly shapesStore = this.taskWorkspaceStore(
    'outputAnnotationInfo',
    // @ts-ignore -- fix me
  )(this.outputInfoStore.state().output.name)('shapes');

  private colorOutput?: AbstractOutputWithAnswerOptions;
  private requiredStaticOutputHelpers: ObjectWith<OutputHelper>;
  private requiredDynamicOutputHelpers: ObjectWith<OutputHelper>;
  private fusionManager: FusionManager;
  private outputId = this.outputInfoStore.state().output.id;

  constructor(
    private eventTrackingService: EventTrackingService,
    private h3mlStore: H3mlStore,
    @Inject(outputInfoStoreToken)
    private outputInfoStore: Store<OutputInfo<AnnotationOutput>>,
    private store: AbstractAnnotationStore,
    private focusedShapeService: ObjectlessFocusedShapeService,
    @Inject(fusionStoreToken)
    private fusionStore: Store<FusionState>,
    fusionManagerFactory: FusionManagerFactory,
  ) {
    this.requiredStaticOutputHelpers = this.getOutputHelpers({
      dynamic: false,
    });
    this.requiredDynamicOutputHelpers = this.getOutputHelpers({
      dynamic: true,
    });

    this.colorOutput = getColorOutput(
      this.outputInfoStore.state().output.meta.nested_output_color_id,
      this.h3mlStore.state().project.outputs,
    );

    this.fusionManager = fusionManagerFactory.getManager(this.outputId);
  }

  getNestedOutput(outputId: number) {
    const nestedOutputRefs = this.outputInfoStore.state().output.answer_options;
    const outputsByName = keyBy(
      this.h3mlStore.state().project.outputs,
      (output) => output.name,
    );
    return nestedOutputRefs
      .map((option: AnswerOption) => outputsByName[option.name])
      .find(matches({ id: outputId }));
  }

  getSummaryOutputsForDisplay(
    output: AbstractOutputWithAnswerOptions = this.outputInfoStore.state()
      .output,
  ) {
    let options = output.answer_options;
    const outputsByName = keyBy(
      this.h3mlStore.state().project.outputs,
      (o) => o.name,
    );
    if (options.length > 1) {
      options = options.filter((option: AnswerOption) => option.label);
    }
    return options.map((option: AnswerOption) => outputsByName[option.name]);
  }

  getLabel$(
    output: Output,
    tagsStore: Store<TagsState>,
    answerOptions = this.outputInfoStore.state().output.answer_options,
  ) {
    return getTag$(
      output,
      tagsStore,
      this.store('frameNumber'),
      answerOptions,
    ).pipe(map((tag) => getOutputHelper(output.type).getLabelFor(tag, output)));
  }

  isParentOptionSelected(parentOutput: AbstractOutput, output: AbstractOutput) {
    const shapeStore = this.focusedShapeService.getFocusedShapeStore();
    assert(shapeStore, 'Must have focused shape');
    const currentTagForParentOutput = this.getCurrentTag(
      shapeStore,
      parentOutput.name,
      this.store.state().frameNumber,
    );
    return this.isTagMatchingDependentOptions(
      currentTagForParentOutput,
      parentOutput,
      output,
    );
  }

  isParentOptionSelected$(
    parentOutput: AbstractOutput,
    output: AbstractOutput,
  ) {
    return this.focusedShapeService.getFocusedShapeStore$().pipe(
      filter(isDefined),
      switchMap((shapeStore) =>
        getTag$(
          parentOutput,
          shapeStore,
          this.store('frameNumber'),
          this.outputInfoStore.state().output.answer_options,
        ),
      ),
      map((tag: string | undefined) =>
        this.isTagMatchingDependentOptions(tag, parentOutput, output),
      ),
    );
  }

  /** @deprecated used for old projects without objects */
  getShapeColor$(shapeStore: Store<ShapeState>) {
    return getShapeColorObjectless$(
      shapeStore,
      this.store('frameNumber'),
      this.outputInfoStore.state().output,
      this.colorOutput,
    );
  }

  getCurrentTag<S extends ShapeState>(
    shapeStore: Store<S>,
    outputName: string,
    frameNumber: number,
  ) {
    const shape = shapeStore.state();
    const options = this.outputInfoStore.state().output.answer_options;
    if (isDynamicOutput(outputName, options)) {
      return getDynamicTag(shape.dynamic_tags[outputName], frameNumber);
    }
    return shape.tags[outputName];
  }

  // eslint-disable-next-line max-lines-per-function,max-params-unless-injectable,complexity -- can't find a logic way to split
  updateTag(
    outputInfo: OutputInfo,
    shapeIndex: number,
    frameNumber: number,
    wasOutputVisible: boolean,
    notifyFusionManager = true,
  ) {
    const outputName = outputInfo.output.name;
    const options = this.outputInfoStore.state().output.answer_options;
    if (
      !this.hasAnswerOption(outputName) ||
      // fix me if you change this code
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
      !this.hasMatchingShape(shapeIndex)
    ) {
      return;
    }

    const shapeStore: Store<TagsState> = this.shapesStore(shapeIndex);

    if (isDynamicOutput(outputName, options)) {
      this.updateDynamicTags(
        outputInfo,
        shapeStore,
        frameNumber,
        wasOutputVisible,
      );
    } else {
      this.updateStaticTags(outputInfo, shapeStore);
    }

    if (notifyFusionManager && this.fusionManager.hasFusedAnswerOptions()) {
      this.fusionManager.updateTag(
        outputInfo,
        shapeIndex,
        frameNumber,
        wasOutputVisible,
        this.outputId,
      );
    }
  }

  removeKeyframe(
    frameNumber: number,
    outputInfo: OutputInfo,
    shapeIndex: number,
    notifyFusionManager = true,
  ) {
    const shapeStore = this.shapesStore(shapeIndex);
    const itemStore = shapeStore('dynamic_tags')(outputInfo.output.name);
    // fix me if you change this code
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    if (!shapeStore.state() || !itemStore.state()) {
      return;
    }
    removeKeyframe(itemStore, frameNumber);

    if (notifyFusionManager && this.fusionManager.hasFusedAnswerOptions()) {
      this.fusionManager.removeKeyframe(
        frameNumber,
        outputInfo,
        shapeIndex,
        this.outputId,
      );
    }
  }

  isShapeMissingRequiredTag(shape: ShapeState) {
    return (
      this.isShapeMissingRequiredStaticTag(shape.tags) ||
      this.isShapeMissingRequiredDynamicTag(shape.dynamic_tags)
    );
  }

  isShapeMissingRequiredTag$(shapeStore: Store<ShapeState>) {
    return combineLatest([
      shapeStore('tags').$.pipe(
        map(
          (tags) =>
            isDefined(tags) && this.isShapeMissingRequiredStaticTag(tags),
        ),
      ),
      shapeStore('dynamic_tags').$.pipe(
        map(
          (dynamicTags) =>
            isDefined(dynamicTags) &&
            this.isShapeMissingRequiredDynamicTag(dynamicTags),
        ),
      ),
    ]).pipe(
      map(
        ([isShapeMissingRequiredStaticTag, isShapeMissingRequiredDynamicTag]) =>
          isShapeMissingRequiredStaticTag || isShapeMissingRequiredDynamicTag,
      ),
    );
  }

  isTagRequiredAndBlank(tag: Answer, outputName: string) {
    const helper =
      // fix me if you change this code
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
      this.requiredStaticOutputHelpers[outputName] ||
      this.requiredDynamicOutputHelpers[outputName];
    // fix me if you change this code
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    return helper ? helper.isAnswerBlank(tag) : false;
  }

  // eslint-disable-next-line complexity -- ignore
  openTagEditor(index: number, source?: string, notifyFusionManager = true) {
    // fix me if you change this code
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    if (!this.hasMatchingShape(index)) {
      return;
    }

    const layerStore = this.getActiveShapeLayerStore();
    const transient = layerStore.state().transient;
    if (
      !this.fusionStore.state().isRightPanelExpanded ||
      transient.tagEditorHidden
    ) {
      layerStore('transient').assign({
        shapeListHidden: true,
        tagEditorHidden: false,
      });
      this.fusionStore('isRightPanelExpanded').set(true);

      if (source) {
        this.eventTrackingService.track({
          id: 'show_tag_editor',
          bucket: 'tag_selection',
          sub_bucket: 'annotation',
          shapes: getShapesForEventTracking(this.shapesStore.state(), [index]),
          source,
        });
      }
    }
    if (notifyFusionManager && this.fusionManager.hasFusedAnswerOptions()) {
      this.fusionManager.openTagEditor(index, this.outputId);
    }
  }

  switchTagEditorTab(
    name: TabName,
    shapeIndex: number,
    notifyFusionManager = true,
  ) {
    // fix me if you change this code
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    if (!this.hasMatchingShape(shapeIndex)) {
      return;
    }
    const layerStore = this.store.getActiveShapeLayerStore();
    if (!layerStore) {
      throw new Error('No active shape layer store');
    }
    layerStore('transient')('tagEditorActiveTab').set(name);

    if (notifyFusionManager && this.fusionManager.hasFusedAnswerOptions()) {
      this.fusionManager.switchTagEditorTab(name, shapeIndex, this.outputId);
    }
  }

  // eslint-disable-next-line complexity -- we didn't come up with a better alternative
  closeTagEditor(
    source?: string,
    shapeIndex?: number,
    notifyFusionManager = true,
  ) {
    // fix me if you change this code
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    if (shapeIndex !== undefined && !this.hasMatchingShape(shapeIndex)) {
      return;
    }
    const layerStore = this.getActiveShapeLayerStore();
    if (!layerStore.state().transient.tagEditorHidden) {
      layerStore('transient').assign({
        shapeListHidden: false,
        tagEditorHidden: true,
      });
      if (source) {
        this.eventTrackingService.track({
          id: 'close_tag_editor',
          bucket: 'tag_selection',
          sub_bucket: 'annotation',
          source,
        });
      }
    }
    if (notifyFusionManager && this.fusionManager.hasFusedAnswerOptions()) {
      this.fusionManager.closeTagEditor(this.outputId, shapeIndex);
    }
  }

  private updateStaticTags(
    outputInfo: OutputInfo,
    shapeStore: Store<TagsState>,
  ) {
    const outputName = outputInfo.output.name;
    if (!outputInfo.isVisible) {
      shapeStore<'tags', AnswersByName>('tags').setUsing((tags) =>
        omit(tags, outputName),
      );
    } else {
      shapeStore<'tags', AnswersByName>('tags')(outputName).set(
        outputInfo.answer,
      );
    }
  }

  /* eslint-disable-next-line complexity, max-lines-per-function -- can't find a logic way to split */
  private updateDynamicTags(
    outputInfo: OutputInfo,
    shapeStore: Store<TagsState>,
    frameNumber: number,
    wasVisible: boolean,
  ) {
    const outputName = outputInfo.output.name;
    const parentOutput = this.getNestedOutput(
      outputInfo.output.depends_on_answers_from_output_id!,
    );
    const answerOptions = this.outputInfoStore.state().output.answer_options;

    if (
      isUndefined(parentOutput) ||
      !hasChangedVisibility(outputInfo, wasVisible)
    ) {
      ensureKeyDynamicTag(shapeStore, outputName, {
        frame_number: frameNumber,
        value: outputInfo.answer,
      });
      return;
    }

    // unsupported parent dynamic outputs, bad things will happen!
    if (isDynamicOutput(parentOutput.name, answerOptions)) {
      if (becameInvisible(outputInfo, wasVisible)) {
        this.removeDynamicKeyframe(shapeStore, outputName, frameNumber);
      }
      return;
    }

    if (becameVisible(outputInfo, wasVisible)) {
      ensureKeyDynamicTag(shapeStore, outputName, {
        frame_number: frameNumber,
        value: outputInfo.answer,
      });
      return;
    }

    if (becameInvisible(outputInfo, wasVisible)) {
      this.removeDynamicTagForAllFrames(shapeStore, outputName);
      return;
    }
  }

  private removeDynamicTagForAllFrames(
    shapeStore: Store<TagsState>,
    outputName: string,
  ) {
    shapeStore('dynamic_tags').setUsing(
      (tags) => omit(tags, outputName) as ObjectWith<FrameSpecificTag[]>,
    );
  }

  private removeDynamicKeyframe(
    shapeStore: Store<TagsState>,
    outputName: string,
    frameNumber: number,
  ) {
    const dynamicTags = shapeStore.state().dynamic_tags[outputName];

    if (dynamicTags) {
      shapeStore('dynamic_tags')(outputName).set(
        dynamicTags.filter((tag) => tag.frame_number !== frameNumber),
      );
    }
  }

  private hasAnswerOption(answerOptionName: string) {
    const options = this.outputInfoStore.state().output.answer_options;
    return options.find((option) => option.name === answerOptionName);
  }

  private getOutputHelpers(options: { dynamic: boolean }) {
    if (!this.answerOptionsAreForNestedOutputs()) {
      return {};
    }

    const answerOptions = this.outputInfoStore.state().output.answer_options;
    const outputInfoByName = this.h3mlStore.state().outputInfoByName;
    const matchingOutputs = answerOptions
      .filter(matches(options))
      .map((option) => outputInfoByName[option.name].output);
    return mapToObject(
      matchingOutputs.filter(matches({ required: true })),
      (output) => [output.name, getOutputHelper(output.type)],
    ) as ObjectWith<OutputHelper>;
  }

  private answerOptionsAreForNestedOutputs() {
    const output = this.outputInfoStore.state().output;
    return (
      !isImageAnnotationOutput(output) ||
      output.meta.layer_type !== 'raster_coding'
    );
  }

  private isShapeMissingRequiredStaticTag(tags: StaticTags) {
    return some(this.requiredStaticOutputHelpers, (outputHelper, outputName) =>
      outputHelper.isAnswerBlank(tags[outputName]),
    );
  }

  private isShapeMissingRequiredDynamicTag(dynamicTags: DynamicTags) {
    return some(
      this.requiredDynamicOutputHelpers,
      (outputHelper, outputName) => {
        const frameSpecificTags = dynamicTags[outputName];
        const hasBlank = some(frameSpecificTags, ({ value }) =>
          outputHelper.isAnswerBlank(value),
        );
        return isEmpty(frameSpecificTags) || hasBlank;
      },
    );
  }

  private isTagMatchingDependentOptions(
    tag: string | undefined,
    parentOutput: AbstractOutput,
    output: AbstractOutput,
  ) {
    if (parentOutput.type === 'TextfieldOutput') {
      const dependsOnTerms = output.meta!.depends_on_terms_from_dictionary;
      assert(dependsOnTerms, 'dependsOnTerms must be defined');
      return dependsOnTerms.some((term) => tag?.includes(term));
    }
    const dependsOnIds = output.meta!.depends_on_answer_option_ids;
    assert(dependsOnIds, 'dependsOnIds must be defined');
    return getOutputHelper(parentOutput.type)
      .getMatchingAnswerOptionIds(tag, parentOutput)
      .some((id) => dependsOnIds.includes(id));
  }

  private getActiveShapeLayerStore() {
    const layerStore = this.store.getActiveShapeLayerStore();
    if (!layerStore) {
      throw new Error('No active shape layer store');
    }
    return layerStore;
  }

  private hasMatchingShape(index: number) {
    return this.shapesStore.state()[index];
  }
}

function becameVisible(outputInfo: OutputInfo, wasVisible: boolean) {
  return outputInfo.isVisible && !wasVisible;
}

function becameInvisible(outputInfo: OutputInfo, wasVisible: boolean) {
  return !outputInfo.isVisible && wasVisible;
}

function hasChangedVisibility(outputInfo: OutputInfo, wasVisible: boolean) {
  return outputInfo.isVisible !== wasVisible;
}
